import React, { useState, useEffect } from 'react';
import axios from 'axios';
import SurveyForm from '../components/SurveyForm';

const ContentView = ({ contentCode, language, onBack }) => {
    const [content, setContent] = useState(null);
    const [password, setPassword] = useState('');
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const [survey, setSurvey] = useState(null);
    const [showSurvey, setShowSurvey] = useState(false);
    const [surveyCompleted, setSurveyCompleted] = useState(false);

    const translations = {
        back: {
            zh: '返回',
            en: 'Back'
        },
        download: {
            zh: '下载',
            en: 'Download'
        },
        password: {
            title: {
                zh: '输入密码',
                en: 'Enter Password'
            },
            placeholder: {
                zh: '请输入密码...',
                en: 'Enter password...'
            },
            submit: {
                zh: '提交',
                en: 'Submit'
            },
            cancel: {
                zh: '取消',
                en: 'Cancel'
            }
        },
        survey: {
            required: {
                zh: '请先完成问卷调查',
                en: 'Please complete the survey first'
            },
            title: {
                zh: '问卷调查',
                en: 'Survey'
            },
            complete: {
                zh: '完成问卷',
                en: 'Complete Survey'
            }
        },
        error: {
            notFound: {
                zh: '内容不存在',
                en: 'Content not found'
            },
            wrongPassword: {
                zh: '密码错误',
                en: 'Wrong password'
            }
        }
    };

    // 加载内容信息
    useEffect(() => {
        const fetchContent = async () => {
            try {
                setLoading(true);
                const response = await axios.get(`/api/controllers/contents.php?action=view&code=${contentCode}`);
                setContent(response.data.content);
                
                // 如果有关联问卷，加载问卷信息
                if (response.data.content.survey_id) {
                    const surveyResponse = await axios.get(`/api/controllers/surveys.php?action=get&id=${response.data.content.survey_id}`);
                    setSurvey(surveyResponse.data.survey);
                }
                
                setError('');
            } catch (err) {
                setError(translations.error.notFound[language]);
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        if (contentCode) {
            fetchContent();
        }
    }, [contentCode, language]);

    // 处理下载
    const handleDownload = () => {
        if (content.is_survey_mandatory && !surveyCompleted && survey) {
            setShowSurvey(true);
            return;
        }
        
        setShowPasswordModal(true);
    };

    // 处理密码提交
    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        
        try {
            setLoading(true);
            const formData = new FormData();
            formData.append('password', password);
            
            if (surveyCompleted && survey) {
                // 如果完成了问卷，将问卷数据添加到请求中
                // 这里假设SurveyForm组件会将问卷数据存储在localStorage中
                const surveyData = JSON.parse(localStorage.getItem(`survey_${survey.id}`));
                formData.append('survey_data', JSON.stringify(surveyData));
            }
            
            const response = await axios.post(
                `/api/controllers/contents.php?action=download&code=${contentCode}`,
                formData
            );
            
            if (response.data.success) {
                // 创建一个临时链接来下载文件
                const link = document.createElement('a');
                link.href = response.data.file.path;
                link.setAttribute('download', response.data.file.name);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                
                setShowPasswordModal(false);
                setPassword('');
                setError('');
            } else {
                setError(translations.error.wrongPassword[language]);
            }
        } catch (err) {
            setError(err.response?.data?.error || translations.error.wrongPassword[language]);
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // 处理问卷完成
    const handleSurveyComplete = (surveyData) => {
        // 将问卷数据存储在localStorage中，以便在下载时使用
        localStorage.setItem(`survey_${survey.id}`, JSON.stringify(surveyData));
        setSurveyCompleted(true);
        setShowSurvey(false);
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">加载中...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="alert alert-danger">
                {error}
                <button className="btn btn-link" onClick={onBack}>
                    {translations.back[language]}
                </button>
            </div>
        );
    }

    if (!content) {
        return null;
    }

    return (
        <div className="content-view">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <button className="btn btn-outline-secondary" onClick={onBack}>
                    <i className="bi bi-arrow-left me-2"></i>
                    {translations.back[language]}
                </button>
                
                <button className="btn btn-primary" onClick={handleDownload}>
                    <i className="bi bi-download me-2"></i>
                    {translations.download[language]}
                </button>
            </div>
            
            <div className="card mb-4">
                <div className="card-body">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                        <h2 className="card-title">{content.title}</h2>
                        <span className="badge bg-primary">{content.custom_code}</span>
                    </div>
                    
                    <p className="card-text">
                        {language === 'zh' 
                            ? (content.description_zh || '暂无描述') 
                            : (content.description_en || 'No description')}
                    </p>
                    
                    {content.is_survey_mandatory && survey && !surveyCompleted && (
                        <div className="alert alert-warning mt-3">
                            <i className="bi bi-exclamation-triangle me-2"></i>
                            {translations.survey.required[language]}
                            <button 
                                className="btn btn-warning btn-sm ms-3"
                                onClick={() => setShowSurvey(true)}
                            >
                                {translations.survey.complete[language]}
                            </button>
                        </div>
                    )}
                </div>
            </div>
            
            {/* 密码模态框 */}
            {showPasswordModal && (
                <div className="modal show d-block" tabIndex="-1">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">{translations.password.title[language]}</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowPasswordModal(false)}
                                ></button>
                            </div>
                            <form onSubmit={handlePasswordSubmit}>
                                <div className="modal-body">
                                    {error && <div className="alert alert-danger">{error}</div>}
                                    
                                    <div className="mb-3">
                                        <input
                                            type="password"
                                            className="form-control"
                                            placeholder={translations.password.placeholder[language]}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => setShowPasswordModal(false)}
                                    >
                                        {translations.password.cancel[language]}
                                    </button>
                                    <button type="submit" className="btn btn-primary">
                                        {translations.password.submit[language]}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
            
            {/* 问卷调查模态框 */}
            {showSurvey && survey && (
                <div className="modal show d-block" tabIndex="-1">
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    {language === 'zh' ? survey.title_zh : survey.title_en}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowSurvey(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <SurveyForm 
                                    survey={survey} 
                                    language={language}
                                    onComplete={handleSurveyComplete}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {/* 模态框背景遮罩 */}
            {(showPasswordModal || showSurvey) && (
                <div className="modal-backdrop show"></div>
            )}
        </div>
    );
};

export default ContentView; 